import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid2,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';
import DevicesIcon from '@mui/icons-material/Devices';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { usePayment } from '../../hooks/usePayment';
import { useDispatch, useSelector } from 'react-redux';
import { themeBackgrounds } from '../../theme/theme';
import { setActivePlanDetails, setPlanInfo } from '../../features/auth/authSlice';
import { httpService } from '../../services/httpService';
import { getCurrencySymbol } from '../../utils/currency';
import { FREE_PLAN_ID } from '../../utils/common';
import { decryptResponse, encryptResponse, getSessionKey, REQ_ENC_KEY } from '../../services';

interface CallingBillDetails {
  total_calls: number;
  audio_calls: number;
  video_calls: number;
  scheduled_audio_calls: number;
  scheduled_video_calls: number;
  total_duration_seconds: number;
  total_duration_minutes: number;
  total_duration_formatted: string;
  audio_call_duration_seconds: number;
  audio_call_duration_minutes: number;
  audio_call_duration_formatted: string;
  video_call_duration_seconds: number;
  video_call_duration_minutes: number;
  video_call_duration_formatted: string;
  scheduled_audio_call_duration_seconds: number;
  scheduled_audio_call_duration_minutes: number;
  scheduled_audio_call_duration_formatted: string;
  scheduled_video_call_duration_seconds: number;
  scheduled_video_call_duration_minutes: number;
  scheduled_video_call_duration_formatted: string;
  total_participants: number;
  unique_users: number;
  date_range: {
    start_date: string;
    end_date: string;
  };
}

interface PriceTier {
  user_tier: string;
  min_users: number;
  max_users?: number;
  monthly: number;
  yearly: number;
}

interface Plan {
  _id: {
    $oid: string;
  };
  plan_id: string;
  plan_name: string;
  description: string;
  currency: string;
  price: PriceTier[];
  features: {
    chat: {
      enabled: boolean;
      message_limit: number;
      file_sharing: boolean;
      max_file_size_mb: number;
    };
    audio_call: {
      enabled: boolean;
      monthly_minutes: number;
      max_participants: number;
      recording_enabled: boolean;
    };
    video_call: {
      enabled: boolean;
      monthly_minutes: number;
      max_participants: number;
      resolution_limit: string;
      recording_enabled: boolean;
    };
  };
  limits: {
    storage_gb: number;
    max_devices: number;
  };
  overage_charges: {
    audio_per_minute: number;
    video_per_minute: number;
    extra_storage_per_gb: number;
    per_extra_user: number;
  };
  validity: {
    type: string;
    duration_days: number;
  };
  status: string;
  created_at: {
    $date: string;
  };
  updated_at: {
    $date: string;
  };
  __v: number;
  is_contact_to_sales?: boolean;
  is_popular?: boolean;
  buttonText?: string;
  buttonVariant?: 'outlined' | 'contained';
}

interface BillingProps {
  selectedPlan: Plan | null;
  selectedTierIndex: number;
  isYearly: boolean;
  isManuallyPlanSelected: boolean;
  setIsManuallyPlanSelected: (value: boolean) => void;
}

const Billing: React.FC<BillingProps> = ({ selectedPlan, selectedTierIndex, isYearly, isManuallyPlanSelected, setIsManuallyPlanSelected }) => {
  const dispatch = useDispatch();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState<any>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [callingDetails, setCallingDetails] = useState<CallingBillDetails | null>(null);
  const [peerCallingLoading, setPeerCallingLoading] = useState(false);
  const [peerCallingError, setPeerCallingError] = useState<string | null>(null);
  const { initiatePayment, error: paymentError, clearError } = usePayment();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationDetails = useSelector((state: any) => state.applicationDetails);
  const existingPlan = useSelector((state: any) => state.auth.userDetails?.activePlanDetails);
  const [isPayBtnDisabled, setIsPayBtnDisabled] = useState(true);
  const [isIncludePlan, setIsIncludePlan] = useState(true);
  const userPlanDetails = userDetails?.plan_details || null;

  // Helper function to format date to yyyy-mm-dd
  const formatDateToYMD = (date: any): string => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    return dateObj.toISOString().split('T')[0];
  };

  const fetchPeerCallingDetails = async () => {
    if (!userDetails?.organization_id || !userPlanDetails) return;

    try {
      setPeerCallingLoading(true);
      setPeerCallingError(null);

      const startDate = formatDateToYMD(userPlanDetails.billing_start_date);
      const endDate = formatDateToYMD(userPlanDetails.billing_end_date);

      if (!startDate || !endDate) {
        setPeerCallingError('Invalid billing dates');
        return;
      }

      const payload = {
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          start_date: startDate,
          end_date: endDate,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<any>('/user/getCallingBillDetails', payload);

      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decryptedData : any = await decryptResponse(data, getSessionKey() as string);
        const callingDetails = decryptedData.calling_bill_details;
        console.log('callingDetails', callingDetails);
        setCallingDetails(callingDetails);
      } else {
        setPeerCallingError(response.data.message || 'Failed to fetch peer calling details');
      }
    } catch (error) {
      console.error('Error fetching peer calling details:', error);
      setPeerCallingError('Failed to fetch peer calling details');
    } finally {
      setPeerCallingLoading(false);
    }
  };

  const fetchOrgPlanInfo = async () => {
    if (!userDetails?.organization_id) return;

    try {
      const payload = {
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post<any>('/user/getOrgPlanDetails', payload);
      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decryptedData : any = await decryptResponse(data, getSessionKey() as string);
        const planInfo = decryptedData.plan;
        dispatch(setPlanInfo(planInfo.plan_details));
        const planPayload = {
          data: await encryptResponse({
            plan_id: planInfo.plan_details.plan_id,
          }, REQ_ENC_KEY),
        };
        const planResponse = await httpService.post<any>(`/user/getActiveOrgPlanById`, planPayload);
        if (planResponse.status === 200 && planResponse.data.data) {
          const data = planResponse.data.data;
          const decryptedData : any = await decryptResponse(data, getSessionKey() as string);
          const planData = decryptedData.plan;
          dispatch(setActivePlanDetails(planData));
        }
      }
    } catch (error) {
      console.error('Error fetching peer calling details:', error);
    }
  };

  // Fetch peer calling details on mount and when plan changes
  useEffect(() => {
    if (userPlanDetails === null) {
      return;
    }
    fetchPeerCallingDetails();
  }, [isManuallyPlanSelected]);

  useEffect(() => {
    // Check if current date is past billing end date
    if (userPlanDetails?.billing_end_date) {
      const currentDate = new Date();
      const billingEndDate = new Date(userPlanDetails.billing_end_date);
      console.log('Current Date:', currentDate);
      console.log('Billing End Date:', billingEndDate);
      if (currentDate > billingEndDate) {
        setIsPayBtnDisabled(false);
        // return;
      } else if (userPlanDetails?.plan_id === FREE_PLAN_ID) {
        setIsPayBtnDisabled(false);
      }
    }

    if (isManuallyPlanSelected) {
      console.log("Plan choose manually...");
      setIsIncludePlan(true);
    } else {
      if (userPlanDetails && userPlanDetails?.billing_cycle === 'yearly') {
        const planExpiryDate = userPlanDetails?.plan_end_date;
        const usageBillingDate = userPlanDetails?.billing_end_date;
        if (planExpiryDate && usageBillingDate) {
          const planDate = new Date(planExpiryDate);
          const usageDate = new Date(usageBillingDate);
          const currentDate = new Date();
          if (planDate.getMonth() === usageDate.getMonth() && planDate.getFullYear() === usageDate.getFullYear()) {
            console.log('Plan expiry and usage billing dates have the same month and year');
          } else {
            const threeDaysBeforeUsage = new Date(usageDate);
            threeDaysBeforeUsage.setDate(usageDate.getDate() - 3);
            if (threeDaysBeforeUsage < currentDate || usageDate < currentDate) {
              // setIsPayBtnDisabled(false);
              setIsIncludePlan(false);
            } else {
              // setIsPayBtnDisabled(true);
              setIsIncludePlan(false);
            }
          }
        }
      }
      if (userPlanDetails && userPlanDetails?.billing_cycle === 'monthly') {
        const currentDate = new Date();
        const usageBillingDate = userPlanDetails?.billing_end_date;
        if (usageBillingDate) {
          const usageDate = new Date(usageBillingDate);
          const threeDaysBeforeUsage = new Date(usageDate);
          threeDaysBeforeUsage.setDate(usageDate.getDate() - 3);
          if (threeDaysBeforeUsage < currentDate || usageDate < currentDate) {
            // setIsPayBtnDisabled(false);
            setIsIncludePlan(true);
            console.log('Current date is 3 days before usage billing date and usage date is in the past');
          } else {
            // setIsPayBtnDisabled(true);
            setIsIncludePlan(false);
          }
        }
      }
    }
  }, [isManuallyPlanSelected, userPlanDetails]);

  // Listen for payment success event
  useEffect(() => {
    const handlePaymentSuccess = async (event: any) => {
      setPaymentResponse(event.detail);
      setPaymentSuccess(true);
      setPaymentLoading(false);
      try {
        await fetchOrgPlanInfo();
        setIsManuallyPlanSelected(false);
        console.log('Plan information updated after successful payment');
      } catch (error) {
        console.error('Failed to update plan information after payment:', error);
      }
    };

    const handlePaymentVerificationFailed = (event: any) => {
      setPaymentResponse(event.detail);
      setPaymentSuccess(false);
      setPaymentLoading(false);
    };

    // Add event listeners
    window.addEventListener('paymentSuccess', handlePaymentSuccess);
    window.addEventListener('paymentVerificationFailed', handlePaymentVerificationFailed);

    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener('paymentSuccess', handlePaymentSuccess);
      window.removeEventListener('paymentVerificationFailed', handlePaymentVerificationFailed);
    };
  }, []);

  // Helper function to get price for selected plan
  const getPlanPrice = (): PriceTier | null => {
    if (!selectedPlan || !selectedPlan.price[selectedTierIndex]) return null;
    return selectedPlan.price[selectedTierIndex];
  };

  // Calculate total cost
  const calculateTotal = () => {
    let baseAmount = 0;
    let totalCallingCost = 0;
    const activePlan = existingPlan;
    const planPrice = selectedPlan ? getPlanPrice() : null;
    if (activePlan) {
      if (selectedPlan && planPrice && isIncludePlan) {
        baseAmount = isYearly ? planPrice.yearly : planPrice.monthly;
      }
      const userLimit = activePlan.price.find((userTier: any) => userTier.user_tier === userPlanDetails?.user_limit);
      const maxUserLimit = userLimit ? userLimit.max_users : 'N/A';
      if (callingDetails) {
        const activeUsersCount = callingDetails.unique_users;
        const audioTotalMinutes = Math.floor(callingDetails.audio_call_duration_seconds / 60);
        const videoTotalMinutes = Math.floor(callingDetails.video_call_duration_seconds / 60);
        const scheduleAudioTotalMinutes = Math.floor(callingDetails.scheduled_audio_call_duration_seconds / 60);
        const scheduleVideoTotalMinutes = Math.floor(callingDetails.scheduled_video_call_duration_seconds / 60);
        const audioCallCost = audioTotalMinutes * activePlan.overage_charges.audio_per_minute;
        const videoCallCost = videoTotalMinutes * activePlan.overage_charges.video_per_minute;
        const scheduleAudioCallCost = scheduleAudioTotalMinutes * activePlan.overage_charges.audio_per_minute;
        const scheduleVideoCallCost = scheduleVideoTotalMinutes * activePlan.overage_charges.video_per_minute;
        const userOverageCost = activeUsersCount > maxUserLimit ? (activeUsersCount - maxUserLimit) * activePlan.overage_charges.per_extra_user : 0;
        console.log('Audio Calls Cost:', audioCallCost, 'Video Calls Cost:', videoCallCost, 'User Overage Cost:', userOverageCost);
        totalCallingCost = audioCallCost + videoCallCost + scheduleAudioCallCost + scheduleVideoCallCost + userOverageCost;
      }
    }
    console.log('Base Amount:', baseAmount+' totalCallingCost Calling Cost:', totalCallingCost);
    return (baseAmount + totalCallingCost);
  };

  // Handle payment
  const handlePayment = async () => {
    if (!selectedPlan) return;

    const planPrice = getPlanPrice();
    if (!planPrice) return;

    try {
      setPaymentLoading(true);
      clearError();

      const paymentData = {
        planId: isIncludePlan ? selectedPlan.plan_id : 'usage_based_billing_plan',
        amount: calculateTotal(),
        currency: selectedPlan.currency,
        planName: selectedPlan.plan_name,
        billingCycle: (isYearly ? 'yearly' : 'monthly') as 'monthly' | 'yearly',
        organizationId: userDetails?.organization_id,
        appId: applicationDetails?.app_id,
        userLength: planPrice.user_tier,
      };

      await initiatePayment(paymentData);
    } catch (error) {
      console.error('Payment failed:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        fontWeight="bold"
        sx={{
          textAlign: 'center',
          mb: 4,
          background: themeBackgrounds.default,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        {selectedPlan ? 'Plan Summary & Payment' : 'Billing & Usage'}
      </Typography>

      {selectedPlan ? (
        // Plan Selection View
        <Grid2 container spacing={4}>
          {/* Selected Plan Details */}
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Card
              sx={{
                mb: 4,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  sx={{
                    color: '#2c3e50',
                    textAlign: 'center',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 60,
                      height: 3,
                      background: themeBackgrounds.default,
                      borderRadius: 2,
                    }
                  }}
                >
                  Selected Plan: {selectedPlan.plan_name}
                </Typography>

                <Grid2 container spacing={3}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Box sx={{
                      p: 3,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: 2,
                      color: 'white',
                      height: '100%',
                    }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon />
                        Plan Details
                      </Typography>
                      <Box sx={{ mt: 2, '& > *': { mb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            User Tier:
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {getPlanPrice()?.user_tier}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Billing:
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {isYearly ? 'Yearly' : 'Monthly'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Base Price:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {getCurrencySymbol(selectedPlan.currency || 'USD')}{getPlanPrice()?.[isYearly ? 'yearly' : 'monthly']}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Box sx={{
                      p: 3,
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      borderRadius: 2,
                      color: 'white',
                      height: '100%',
                    }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StorageIcon />
                        Plan Limits
                      </Typography>
                      <Box sx={{ mt: 2, '& > *': { mb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StorageIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Storage:
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedPlan.limits.storage_gb} GB
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DevicesIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Max Devices:
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedPlan.limits.max_devices}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Validity:
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {isYearly ? "1 Year" : "1 Month"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid2>
                </Grid2>
              </CardContent>
            </Card>

            {/* Payment Summary */}
            <Card
              sx={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  sx={{
                    color: '#2c3e50',
                    textAlign: 'center',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 60,
                      height: 3,
                      background: themeBackgrounds.default,
                      borderRadius: 2,
                    }
                  }}
                >
                  Payment Summary
                </Typography>

                <Box sx={{ mb: 3 }}>
                  {selectedPlan && getPlanPrice() && isIncludePlan && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography>Base Plan ({isYearly ? 'Yearly' : 'Monthly'})</Typography>
                      <Typography>{getCurrencySymbol(selectedPlan.currency || 'USD')}{getPlanPrice()?.[isYearly ? 'yearly' : 'monthly']}</Typography>
                    </Box>
                  )}

                  {/* Peer Calling Details Section */}
                  {callingDetails && (() => {
                    const activePlan = existingPlan;
                    if (!activePlan) return null;
                    console.log('Active Plan:', activePlan);
                    const userLimit = activePlan.price.find((userTier: any) => userTier.user_tier === userPlanDetails?.user_limit);
                    const maxUserLimit = userLimit ? userLimit.max_users : 'N/A';

                    // Calculate using seconds for accurate billing
                    const audioTotalMinutes = Math.floor(callingDetails.audio_call_duration_seconds / 60);
                    const videoTotalMinutes = Math.floor(callingDetails.video_call_duration_seconds / 60);
                    const scheduleAudioTotalMinutes = Math.floor(callingDetails.scheduled_audio_call_duration_seconds / 60);
                    const scheduleVideoTotalMinutes = Math.floor(callingDetails.scheduled_video_call_duration_seconds / 60);
                    const activeUsersCount = callingDetails.unique_users;
                    const audioPeerCost = Math.round(audioTotalMinutes * activePlan.overage_charges.audio_per_minute * 100) / 100;
                    const videoPeerCost = Math.round(videoTotalMinutes * activePlan.overage_charges.video_per_minute * 100) / 100;
                    const scheduleAudioCallCost = Math.round(scheduleAudioTotalMinutes * activePlan.overage_charges.audio_per_minute * 100) / 100;
                    const scheduleVideoCallCost = Math.round(scheduleVideoTotalMinutes * activePlan.overage_charges.video_per_minute * 100) / 100;

                    return (
                      <>
                        {audioPeerCost > 0 && (
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Audio Calls ({callingDetails.audio_calls} calls, {callingDetails.audio_call_duration_formatted})</Typography>
                            <Typography>{getCurrencySymbol(selectedPlan.currency || 'USD')}{audioPeerCost}</Typography>
                          </Box>
                        )}
                        {videoPeerCost > 0 && (
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Video Calls ({callingDetails.video_calls} calls, {callingDetails.video_call_duration_formatted})</Typography>
                            <Typography>{getCurrencySymbol(selectedPlan.currency || 'USD')}{videoPeerCost}</Typography>
                          </Box>
                        )}
                        {scheduleAudioCallCost > 0 && (
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Schedule Audio Calls ({callingDetails.scheduled_audio_calls} calls, {callingDetails.scheduled_audio_call_duration_formatted})</Typography>
                            <Typography>{getCurrencySymbol(selectedPlan.currency || 'USD')}{scheduleAudioCallCost}</Typography>
                          </Box>
                        )}
                        {scheduleVideoCallCost > 0 && (
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Schedule Video Calls ({callingDetails.scheduled_video_calls} calls, {callingDetails.scheduled_video_call_duration_formatted})</Typography>
                            <Typography>{getCurrencySymbol(selectedPlan.currency || 'USD')}{scheduleVideoCallCost}</Typography>
                          </Box>
                        )}
                        {activeUsersCount > maxUserLimit && (
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Active Users Overage ({activeUsersCount} active users, limit {maxUserLimit})</Typography>
                            <Typography>{getCurrencySymbol(selectedPlan.currency || 'USD')}{Math.round((activeUsersCount - maxUserLimit) * activePlan.overage_charges.per_extra_user * 100) / 100}</Typography>
                          </Box>
                        )}
                      </>
                    );
                  })()}

                  {peerCallingLoading && (
                    <Box display="flex" justifyContent="center" mb={1}>
                      <Typography variant="body2" color="text.secondary">Loading calling details...</Typography>
                    </Box>
                  )}

                  {peerCallingError && (
                    <Alert severity="warning" sx={{ mb: 1, fontSize: '0.85rem' }}>
                      {peerCallingError}
                    </Alert>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="h6" fontWeight="bold">Total Amount</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {getCurrencySymbol(selectedPlan.currency || 'USD')}{calculateTotal().toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                {paymentError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {paymentError}
                  </Alert>
                )}

                {paymentSuccess && paymentResponse && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Payment Successful!
                    </Typography>
                    <Typography variant="body2">
                      Payment ID: {paymentResponse.razorpay_payment_id}
                    </Typography>
                    <Typography variant="body2">
                      Order ID: {paymentResponse.razorpay_order_id}
                    </Typography>
                    {paymentResponse.verified && (
                      <Typography variant="body2" color="success.main">
                        ✓ Payment verified successfully
                      </Typography>
                    )}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handlePayment}
                  disabled={paymentLoading || paymentSuccess || isPayBtnDisabled}
                  sx={{
                    background: themeBackgrounds.default,
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    borderRadius: 3,
                    boxShadow: '0 8px 25px rgba(0, 122, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: themeBackgrounds.default,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 35px rgba(0, 122, 255, 0.4)',
                    },
                    '&:disabled': {
                      background: '#ccc',
                      color: '#666',
                    },
                  }}
                >
                  {paymentLoading ? 'Processing Payment...' : paymentSuccess ? 'Payment Completed' : `Pay ${getCurrencySymbol(selectedPlan.currency || 'USD')}${calculateTotal().toFixed(2)}`}
                </Button>
              </CardContent>
            </Card>
          </Grid2>

          {/* Plan Features Summary */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  mb={3}
                  sx={{
                    color: '#2c3e50',
                    textAlign: 'center',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 40,
                      height: 3,
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      borderRadius: 2,
                    }
                  }}
                >
                  Plan Features
                </Typography>

                <Accordion
                  sx={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: 2,
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#667eea' }} />}
                    sx={{
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.04)',
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, color: '#2c3e50' }}>Chat Features</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ background: '#ffffff', borderRadius: '0 0 8px 8px' }}>
                    <Typography variant="body2" sx={{ color: '#495057', lineHeight: 1.6 }}>
                      Messages: {selectedPlan.features.chat.message_limit}<br />
                      File Sharing: {selectedPlan.features.chat.file_sharing ? 'Yes' : 'No'}<br />
                      Max File Size: {selectedPlan.features.chat.max_file_size_mb}MB
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: 2,
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#667eea' }} />}
                    sx={{
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.04)',
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, color: '#2c3e50' }}>Audio Call Features</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ background: '#ffffff', borderRadius: '0 0 8px 8px' }}>
                    <Typography variant="body2" sx={{ color: '#495057', lineHeight: 1.6 }}>
                      Monthly Minutes: {selectedPlan.features.audio_call.monthly_minutes}<br />
                      Max Participants: {selectedPlan.features.audio_call.max_participants}<br />
                      Recording: {selectedPlan.features.audio_call.recording_enabled ? 'Yes' : 'No'}
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: 2,
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#667eea' }} />}
                    sx={{
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.04)',
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, color: '#2c3e50' }}>Video Call Features</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ background: '#ffffff', borderRadius: '0 0 8px 8px' }}>
                    <Typography variant="body2" sx={{ color: '#495057', lineHeight: 1.6 }}>
                      Monthly Minutes: {selectedPlan.features.video_call.monthly_minutes}<br />
                      Max Participants: {selectedPlan.features.video_call.max_participants}<br />
                      Resolution: {selectedPlan.features.video_call.resolution_limit}<br />
                      Recording: {selectedPlan.features.video_call.recording_enabled ? 'Yes' : 'No'}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            mx: { xs: 2, md: 4 },
          }}
        >
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              fontSize: '1.2rem',
              fontWeight: 500,
              mb: 2,
            }}
          >
            Please select a plan from the Plans tab to view billing information.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              opacity: 0.7,
            }}
          >
            Choose the perfect plan for your communication needs
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Billing;
